import { type FC, type MouseEventHandler, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';

import { FormattedMessage } from 'dibs-react-intl';
import { TradeBenefitsHeroActionLink } from './TradeBenefitsHeroActionLink.tsx';

import { useServerVars } from '../../../shared/ServerVarsContext.tsx';
import { CURRENT_TIER_SECTION_ID } from '../../tradeBenefitsConstants.ts';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { type TradeBenefitsHeroActions_viewer$key } from './__generated__/TradeBenefitsHeroActions_viewer.graphql.ts';

import styles from './TradeBenefitsHeroActions.scss';
import dibsCss from 'dibs-css';

type ActionTarget = { href: string } | { onClick: MouseEventHandler };

type Action = ActionTarget & {
    key: string;
    trigger: string;
    title: ReactNode;
    mobileTitle: ReactNode;
    description: ReactNode;
};

const viewerFragment = graphql`
    fragment TradeBenefitsHeroActions_viewer on Viewer {
        allConversations: conversationsSolr(buyerId: $userId, showInactiveUsers: false) {
            facets {
                buyerTags {
                    unread
                    activePrivateOffer
                    activeHold
                }
            }
        }
    }
`;

const boldCount = (count: number): ReactNode => (
    <span className={dibsCss.textBodySmallHeavy}>{count}</span>
);

const TradeBenefitsHeroActions: FC<{
    viewer: TradeBenefitsHeroActions_viewer$key;
    isFirmless: boolean;
}> = ({ viewer: viewerRef, isFirmless }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tags = viewer?.allConversations?.facets?.buyerTags;
    const { isMobile } = useServerVars();

    const scrollToCurrentTier: MouseEventHandler = () => {
        const el = document.getElementById(CURRENT_TIER_SECTION_ID);
        if (el) {
            scrollIntoView(el, { time: 600 });
        }
    };

    const actions: Action[] = [
        {
            key: 'messages',
            trigger: 'messages',
            href: '/inbox/?filter=unread',
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.messages.title"
                    defaultMessage="View Your Messages"
                />
            ),
            mobileTitle: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.messages.mobileTitle"
                    defaultMessage="Messages"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.messages.description"
                    defaultMessage="You have {count} unread {count, plural, one {message} other {messages}}"
                    values={{ count: boldCount(tags?.unread || 0) }}
                />
            ),
        },
        {
            key: 'offers',
            trigger: 'active-offers',
            href: '/inbox/?filter=activePrivateOffer',
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.offers.title"
                    defaultMessage="View Your Active Offers"
                />
            ),
            mobileTitle: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.offers.mobileTitle"
                    defaultMessage="Active Offers"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.offers.description"
                    defaultMessage="You have {count} active {count, plural, one {offer} other {offers}}"
                    values={{ count: boldCount(tags?.activePrivateOffer || 0) }}
                />
            ),
        },
        {
            key: 'holds',
            trigger: 'holds',
            href: '/inbox/?filter=activeHold',
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.holds.title"
                    defaultMessage="View Your Items on Hold"
                />
            ),
            mobileTitle: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.holds.mobileTitle"
                    defaultMessage="Items on Hold"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.holds.description"
                    defaultMessage="You have {count} {count, plural, one {item} other {items}} on hold"
                    values={{ count: boldCount(tags?.activeHold || 0) }}
                />
            ),
        },
        {
            key: 'orders',
            trigger: 'orders',
            href: '/orders/',
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.orders.title"
                    defaultMessage="View Your Orders"
                />
            ),
            mobileTitle: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.orders.mobileTitle"
                    defaultMessage="Orders"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.orders.description"
                    defaultMessage="Review your active and confirmed orders"
                />
            ),
        },
        {
            key: 'savedItems',
            trigger: 'favorites',
            href: '/favorites/items/',
            title: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.savedItems.title"
                    defaultMessage="View Your Saved Items"
                />
            ),
            mobileTitle: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.savedItems.mobileTitle"
                    defaultMessage="Saved Items"
                />
            ),
            description: (
                <FormattedMessage
                    id="abt.TradeBenefitsHeroActions.savedItems.description"
                    defaultMessage="Revisit your favorites"
                />
            ),
        },
    ];

    const allBenefitsTitle = (
        <FormattedMessage
            id="abt.TradeBenefitsHeroActions.allBenefits.title"
            defaultMessage="All Benefits"
        />
    );

    const visibleActions = [
        ...(isFirmless ? actions.filter(action => action.key !== 'orders') : actions),
        ...(isMobile && !isFirmless
            ? [
                  {
                      key: 'allBenefits',
                      trigger: 'all-benefits',
                      onClick: scrollToCurrentTier,
                      title: allBenefitsTitle,
                      mobileTitle: allBenefitsTitle,
                      description: null,
                  },
              ]
            : []),
    ];

    return (
        <div
            className={classnames(
                styles.container,
                dibsCss.grid,
                dibsCss.gapXsmall,
                dibsCss.contentBetween
            )}
        >
            {visibleActions.map(action => {
                const { key, trigger, title, mobileTitle, description } = action;
                const linkTitle = isMobile ? mobileTitle : title;

                const trackClick = (): void => {
                    trackTradeBenefitsEvent({
                        stepInteractionName: 'action block clicked',
                        trigger,
                    });
                };

                if ('href' in action) {
                    return (
                        <TradeBenefitsHeroActionLink
                            key={key}
                            href={action.href}
                            title={linkTitle}
                            description={description}
                            onClick={trackClick}
                        />
                    );
                }

                return (
                    <TradeBenefitsHeroActionLink
                        key={key}
                        onClick={event => {
                            trackClick();
                            action.onClick(event);
                        }}
                        title={linkTitle}
                        description={description}
                    />
                );
            })}
        </div>
    );
};

export { TradeBenefitsHeroActions };
